import { useSeo } from "@/lib/seo";
import { useLotCalculator } from './useLotCalculator';
import LotCalculator from './components/LotCalculator';
import ExplanationSection from './components/ExplanationSection';
import FormulaSection from './components/FormulaSection';
import ExampleSection from './components/ExampleSection';
import { MistakesSection } from './components/InfoSections';
import BrokerSection from './components/BrokerSection';
import Reveal from './components/Reveal';
import AppHeader from '@/pages/home/components/AppHeader';
import Footer from '@/pages/home/components/Footer';
import AppEnding from '@/pages/home/components/AppEnding';

export default function LotSizePage() {
  useSeo({
    /* @replace:lotsize-title */
    title: 'Máy tính Khối lượng Giao dịch Forex | Quản lý Vốn & Rủi ro Tối ưu - ForexMatrix',
    /* @replace:lotsize-description */
    description: 'Sử dụng Máy tính Khối lượng (Position Size Calculator) của ForexMatrix để xác định chính xác số Lot giao dịch dựa trên số dư tài khoản, mức Stop Loss và tỷ lệ rủi ro. Công cụ Fintech thiết yếu giúp bạn quản trị vốn và bảo vệ tài khoản chuyên nghiệp.',
    /* @replace:lotsize-keywords */
    keywords: 'máy tính khối lượng forex, tính số lot forex, quản lý vốn forex, position size calculator, tính toán rủi ro forex, công cụ tính lot, forex matrix',
    path: '/may-tinh-khoi-luong',
  });
  const calc = useLotCalculator();

  return (
    <div className="relative min-h-screen bg-[#F8FAFC] text-slate-900">
      <AppHeader />
      <main className="pb-8">
        <div className="mx-auto max-w-6xl px-4 pt-28 pb-16 md:px-8 md:pt-32 md:pb-20">
          {/* Intro */}
          <Reveal>
            <section className="rounded-3xl border border-slate-200/80 bg-white p-10 shadow-sm lg:p-12">
              <span className="mb-6 inline-block rounded-full bg-purple-50 px-3 py-1.5 text-xs font-bold tracking-wider text-purple-600">
                QUẢN TRỊ RỦI RO CHUYÊN SÂU
              </span>

              <div className="flex flex-col items-start justify-between gap-12 lg:flex-row">
                {/* Left */}
                <div className="w-full lg:w-7/12">
                  <h1 className="text-3xl font-extrabold leading-tight text-slate-900 lg:text-4xl">
                    Tính toán khối lượng chuẩn xác — Khóa chặt rủi ro mọi lệnh giao dịch
                  </h1>
                  <p className="mt-4 text-base leading-relaxed text-slate-600">
                    Khối lượng giao dịch (Lot) quyết định mỗi Pip biến động ảnh hưởng bao nhiêu đến tài khoản của
                    bạn. Chọn đúng khối lượng là bước đầu tiên để giao dịch có kỷ luật và bền vững, thay vì đặt cược
                    quá lớn so với số vốn đang có.
                  </p>
                  <div className="mt-8">
                    <a
                      href="#calculator"
                      className="inline-block rounded-xl bg-purple-600 px-6 py-3.5 text-center text-sm font-bold text-white shadow-sm transition-all hover:bg-purple-700"
                    >
                      Bắt đầu tính toán ngay bên dưới ↓
                    </a>
                  </div>
                </div>

                {/* Right features */}
                <div className="flex w-full flex-col gap-5 border-t border-slate-100 pt-8 lg:w-5/12 lg:border-l lg:border-t-0 lg:pl-10 lg:pt-0">
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-purple-50 p-3 text-purple-600">
                      <i className="ri-shield-check-line text-xl" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Bảo vệ tài khoản</p>
                      <p className="mt-1 text-xs leading-normal text-slate-500">
                        Giới hạn số tiền rủi ro cho mỗi lệnh ở mức bạn kiểm soát được.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-purple-50 p-3 text-purple-600">
                      <i className="ri-equalizer-line text-xl" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Nhất quán rủi ro</p>
                      <p className="mt-1 text-xs leading-normal text-slate-500">
                        Áp dụng cùng một tỷ lệ rủi ro cho mọi giao dịch để kết quả ổn định.
                      </p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-purple-50 p-3 text-purple-600">
                      <i className="ri-line-chart-line text-xl" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-slate-900">Tối ưu hiệu suất</p>
                      <p className="mt-1 text-xs leading-normal text-slate-500">
                        Khối lượng phù hợp giúp lợi nhuận tăng trưởng đều mà không cần mạo hiểm.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </section>
          </Reveal>

          {/* Core calculator */}
          <div className="mt-10" id="calculator">
            <Reveal delay={80}>
              <LotCalculator calc={calc} />
            </Reveal>
          </div>

          {/* Content modules */}
          <div className="mt-16 space-y-16">
            <Reveal>
              <FormulaSection />
            </Reveal>
            <Reveal>
              <ExampleSection />
            </Reveal>
            <Reveal>
              <MistakesSection />
            </Reveal>
            <Reveal>
              <BrokerSection />
            </Reveal>
          </div>
        </div>
      </main>
      <Footer />
      <AppEnding />

      <ExplanationSection calc={calc} open={calc.explanationOpen} onClose={calc.closeExplanation} />
    </div>
  );
}